import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import {
  MIXCLOUD_USERNAME,
  MIXCLOUD_API_BASE,
  PLAYLISTS_CONFIG_URL,
  ALL_EPISODES_SLUG,
} from '../config/mixcloud';
import '../styles/CatchupPage.css';

const MIXCLOUD_CONFIG = {
  username: 'utterradio',
  pageSize: 100,
};

const MIXCLOUD_USER_API = `${MIXCLOUD_API_BASE}/${MIXCLOUD_CONFIG.username}/`;
const MIXCLOUD_CLOUDCASTS_API =
  `${MIXCLOUD_API_BASE}/${MIXCLOUD_CONFIG.username}/cloudcasts/` +
  `?limit=${MIXCLOUD_CONFIG.pageSize}`;

/* -------------------------------------------------------------------------
 *  Helpers
 * ---------------------------------------------------------------------- */

const createSlug = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

const formatMixcloudDuration = (seconds) => {
  if (!seconds || Number.isNaN(seconds)) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) {
    return `${h}:${m < 10 ? '0' : ''}${m}:${s < 10 ? '0' : ''}${s}`;
  }
  return `${m}:${s < 10 ? '0' : ''}${s}`;
};

const shareEpisode = async (showTitle, episodeTitle, showSlug, episodeSlug) => {
  const shareUrl = `${window.location.origin}/catchup/${showSlug}/${episodeSlug}`;
  const shareText = `Check out "${episodeTitle}" from ${showTitle} on Utter Radio!`;

  if (navigator.share) {
    try {
      await navigator.share({
        title: `${episodeTitle} - ${showTitle}`,
        text: shareText,
        url: shareUrl,
      });
    } catch (err) {
      console.log('Share failed:', err);
    }
  } else {
    try {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      alert('Link copied to clipboard!');
    } catch (err) {
      prompt('Copy this link:', shareUrl);
    }
  }
};

/* -------------------------------------------------------------------------
 *  Component
 * ---------------------------------------------------------------------- */

export const CatchupPage = () => {
  const [playlists, setPlaylists] = useState([]);
  const [allEpisodes, setAllEpisodes] = useState([]);
  const [currentPlaylistIndex, setCurrentPlaylistIndex] = useState(-1);
  const [currentEpisodeIndex, setCurrentEpisodeIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [playerImage, setPlayerImage] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { playlistSlug, episodeSlug } = useParams();

  const {
    isPlaying: globalIsPlaying,
    isCatchupMode,
    loadMixcloudEpisode,
    setMixcloudOnEnded,
    trackInfo,
  } = useAudioPlayer();

  const CONFIG = {
    defaultImage: '/images/UtterRadioLogoFull.jpg',
  };

  // -------- Refs --------
  const lastExplicitPlayerImageRef = useRef(null);
  const loadEpisodeRef = useRef(null);
  const nextEpisodeRef = useRef(null);

  // True after the initial Mixcloud fetch completes.
  const hasFetchedRef = useRef(false);

  // Set right before an internal navigate(). Consumed by the URL effect so
  // internal clicks don't get re-processed as if they were external links.
  const skipUrlEffectRef = useRef(false);

  // -------- Image helpers (unchanged) --------
  const normalizeImage = useCallback(
    (url) => {
      if (!url) return CONFIG.defaultImage;
      if (/^(data:|blob:)/i.test(url)) return url;
      if (url.startsWith('//')) return `${window.location.protocol}${url}`;
      if (/^https?:\/\//i.test(url)) return url;
      if (url.startsWith('/')) return `${window.location.origin}${url}`;
      return url;
    },
    [CONFIG.defaultImage]
  );

  const preloadAndSetImage = useCallback(
    (url) => {
      const resolvedUrl = normalizeImage(url);
      if (!resolvedUrl) {
        setPlayerImage(CONFIG.defaultImage);
        lastExplicitPlayerImageRef.current = CONFIG.defaultImage;
        return;
      }
      if (resolvedUrl === lastExplicitPlayerImageRef.current) return;

      const img = new Image();
      let settled = false;

      img.onload = () => {
        if (settled) return;
        settled = true;
        setPlayerImage(resolvedUrl);
        lastExplicitPlayerImageRef.current = resolvedUrl;
      };

      img.onerror = () => {
        if (settled) return;
        settled = true;
        if (lastExplicitPlayerImageRef.current !== CONFIG.defaultImage) {
          setPlayerImage(CONFIG.defaultImage);
          lastExplicitPlayerImageRef.current = CONFIG.defaultImage;
        }
      };

      img.src = resolvedUrl;

      setTimeout(() => {
        if (!settled && img.complete && img.naturalWidth) {
          settled = true;
          setPlayerImage(resolvedUrl);
          lastExplicitPlayerImageRef.current = resolvedUrl;
        }
      }, 50);
    },
    [normalizeImage, CONFIG.defaultImage]
  );

  // -------- Resolve a "playlist index" to its object --------
  const getPlaylistAt = useCallback(
    (index) => {
      if (index === -1) {
        return {
          title: 'All Episodes',
          description: 'Every episode uploaded to Mixcloud.',
          image: allEpisodes[0]?.image || '',
          slug: ALL_EPISODES_SLUG,
          episodes: allEpisodes,
        };
      }
      return playlists[index] || {};
    },
    [playlists, allEpisodes]
  );

  // -------- Internal navigation helper --------
  // Only marks the URL effect to skip if the path actually changes.
  // Prevents the "stuck flag" problem when clicking the same link twice.
  const navigateInternally = useCallback(
    (path) => {
      if (location.pathname === path) return;
      skipUrlEffectRef.current = true;
      navigate(path, { replace: true });
    },
    [navigate, location.pathname]
  );

  // =======================================================================
  //  LOAD AN EPISODE (unchanged logic, uses navigateInternally)
  // =======================================================================
  const loadEpisode = useCallback(
    async (playlistIndex, episodeIndex) => {
      const playlist = getPlaylistAt(playlistIndex);
      const episode = playlist?.episodes?.[episodeIndex];
      if (!playlist || !episode) return;

      setCurrentPlaylistIndex(playlistIndex);
      setCurrentEpisodeIndex(episodeIndex);

      navigateInternally(`/catchup/${playlist.slug}/${episode.slug}`);

      let chosenImage;
      if (episode.image && episode.image !== CONFIG.defaultImage) {
        chosenImage = episode.image;
      } else if (playlist.image && playlist.image !== CONFIG.defaultImage) {
        chosenImage = playlist.image;
      } else {
        chosenImage = CONFIG.defaultImage;
      }
      preloadAndSetImage(chosenImage);

      if (episode.cloudcastKey) {
        await loadMixcloudEpisode(
          episode.cloudcastKey,
          episode.title,
          playlist.title,
          chosenImage,
          true
        );
      }
    },
    [
      getPlaylistAt,
      navigateInternally,
      preloadAndSetImage,
      loadMixcloudEpisode,
      CONFIG.defaultImage,
    ]
  );

  useEffect(() => {
    loadEpisodeRef.current = loadEpisode;
  }, [loadEpisode]);

  // =======================================================================
  //  APPLY URL SELECTION
  //  Pure state-update routine: takes the loaded data as args (so it can be
  //  called from the mount effect before React commits state) and sets the
  //  current playlist / episode indices from the URL params.
  // =======================================================================
  const applyUrlSelection = useCallback(
    (episodesList, playlistsList) => {
      // Case A: All Episodes
      if (playlistSlug === ALL_EPISODES_SLUG) {
        setCurrentPlaylistIndex(-1);

        if (episodeSlug) {
          const idx = episodesList.findIndex((ep) => ep.slug === episodeSlug);
          if (idx >= 0) {
            setCurrentEpisodeIndex(idx);
            // Defer so loadEpisode can read the freshest state
            setTimeout(() => loadEpisodeRef.current?.(-1, idx), 100);
          }
        }

        const img = episodesList[0]?.image || CONFIG.defaultImage;
        preloadAndSetImage(img);
        lastExplicitPlayerImageRef.current = img;
        return;
      }

      // Case B: a specific playlist
      if (playlistSlug) {
        const pIdx = playlistsList.findIndex((p) => p.slug === playlistSlug);
        if (pIdx >= 0) {
          setCurrentPlaylistIndex(pIdx);

          if (episodeSlug) {
            const eps = playlistsList[pIdx].episodes;
            const eIdx = eps.findIndex((ep) => ep.slug === episodeSlug);
            if (eIdx >= 0) {
              setCurrentEpisodeIndex(eIdx);
              setTimeout(() => loadEpisodeRef.current?.(pIdx, eIdx), 100);
            }
          }

          const img =
            playlistsList[pIdx].image || CONFIG.defaultImage;
          preloadAndSetImage(img);
          lastExplicitPlayerImageRef.current = img;
          return;
        }
      }

      // Default: All Episodes
      setCurrentPlaylistIndex(-1);
      const img =
        episodesList[0]?.image ||
        playlistsList[0]?.image ||
        CONFIG.defaultImage;
      preloadAndSetImage(img);
      lastExplicitPlayerImageRef.current = img;
    },
    [
      playlistSlug,
      episodeSlug,
      preloadAndSetImage,
      CONFIG.defaultImage,
    ]
  );

  // =======================================================================
//  INITIAL DATA FETCH — runs once (StrictMode-safe)
// =======================================================================
useEffect(() => {
  let isActive = true;

  (async () => {
    // Guard against a *completed* prior fetch — but don't flip this
    // before the fetch finishes, or StrictMode's remount will skip it.
    if (hasFetchedRef.current) return;

    setIsLoading(true);
    setError(null);

    try {
      // 1. Fetch user profile
      const userRes = await fetch(MIXCLOUD_USER_API, { cache: 'no-store' });
      if (!userRes.ok) throw new Error('Failed to fetch Mixcloud user');
      const userData = await userRes.json();

      // 2. Fetch all cloudcasts (paginated)
      let allCloudcasts = [];
      let nextUrl = MIXCLOUD_CLOUDCASTS_API;

      while (nextUrl) {
        const res = await fetch(nextUrl, { cache: 'no-store' });
        if (!res.ok) throw new Error('Failed to fetch cloudcasts');
        const page = await res.json();
        allCloudcasts = allCloudcasts.concat(page.data || []);
        nextUrl = page.paging?.next || null;
        if (allCloudcasts.length >= 1000) break;
      }

      // 3. Normalize each cloudcast
      const episodeByKey = {};
      const episodesList = [];

      allCloudcasts.forEach((cloudcast) => {
        const epTitle = cloudcast.name || 'Untitled';
        const ep = {
          title: epTitle,
          description: cloudcast.description || '',
          duration: formatMixcloudDuration(cloudcast.audio_length),
          image: normalizeImage(
            cloudcast.pictures?.large ||
              cloudcast.pictures?.medium ||
              userData.pictures?.large ||
              ''
          ),
          cloudcastKey: cloudcast.key,
          cloudcastUrl: cloudcast.url,
          createdTime: cloudcast.created_time,
          slug: createSlug(epTitle),
        };
        episodeByKey[cloudcast.key] = ep;
        episodesList.push(ep);
      });

      episodesList.sort((a, b) => {
        const tA = new Date(a.createdTime || 0).getTime();
        const tB = new Date(b.createdTime || 0).getTime();
        return tB - tA;
      });

      // 4. Fetch playlists.json
      const playlistRes = await fetch(PLAYLISTS_CONFIG_URL, {
        cache: 'no-store',
      });
      if (!playlistRes.ok) throw new Error('Failed to fetch playlists.json');
      const playlistConfig = await playlistRes.json();
      const playlistDefs = Array.isArray(playlistConfig?.playlists)
        ? playlistConfig.playlists
        : [];

      const normalizedPlaylists = playlistDefs.map((pl) => {
        const plTitle = pl.title || 'Untitled Playlist';
        const plSlug = createSlug(plTitle);

        const eps = (pl.episodes || [])
          .map((key) => episodeByKey[key])
          .filter(Boolean)
          .map((ep) => ({ ...ep, showSlug: plSlug }));

        return {
          title: plTitle,
          description: pl.description || '',
          image: normalizeImage(pl.image || ''),
          slug: plSlug,
          episodes: eps,
        };
      });

      if (!isActive) return;

      // 5. Commit state
      setAllEpisodes(episodesList);
      setPlaylists(normalizedPlaylists);
      hasFetchedRef.current = true; // ← only flip once we've actually committed

      // 6. Apply URL selection using the freshly-fetched data
      applyUrlSelection(episodesList, normalizedPlaylists);
    } catch (err) {
      if (!isActive) return;
      console.error('Error loading playlists:', err);
      setError('Failed to load playlists. Please try again.');
    } finally {
      if (isActive) setIsLoading(false);
    }
  })();

  return () => {
    isActive = false;
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  // =======================================================================
  //  URL CHANGE HANDLING — runs on every URL param change AFTER the initial
  //  fetch. Uses the already-loaded data, so no network calls happen here.
  // =======================================================================
  useEffect(() => {
    // Wait until the initial fetch has completed and state has data
    if (!hasFetchedRef.current) return;
    if (isLoading) return;
    if (allEpisodes.length === 0 && playlists.length === 0) return;

    // Skip internal navigations (clicking an episode / playlist)
    if (skipUrlEffectRef.current) {
      skipUrlEffectRef.current = false;
      return;
    }

    applyUrlSelection(allEpisodes, playlists);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlistSlug, episodeSlug]);

  // =======================================================================
  //  SELECTION HANDLERS
  // =======================================================================
  const handleSelectAllEpisodes = () => {
    setCurrentPlaylistIndex(-1);
    setCurrentEpisodeIndex(-1);
    const img = allEpisodes[0]?.image || CONFIG.defaultImage;
    preloadAndSetImage(img);
    lastExplicitPlayerImageRef.current = img;
    navigateInternally(`/catchup/${ALL_EPISODES_SLUG}`);
  };

  const handleSelectPlaylist = (index) => {
    setCurrentPlaylistIndex(index);
    setCurrentEpisodeIndex(-1);
    const pl = playlists[index];
    const img = pl?.image || CONFIG.defaultImage;
    preloadAndSetImage(img);
    lastExplicitPlayerImageRef.current = img;
    navigateInternally(`/catchup/${pl.slug}`);
  };

  // =======================================================================
  //  PREV / NEXT
  // =======================================================================
  const prevEpisode = useCallback(() => {
    const playlist = getPlaylistAt(currentPlaylistIndex);
    if (!playlist?.episodes) return;
    if (currentEpisodeIndex < playlist.episodes.length - 1) {
      loadEpisode(currentPlaylistIndex, currentEpisodeIndex + 1);
    } else if (currentPlaylistIndex > -1) {
      loadEpisode(currentPlaylistIndex - 1, 0);
    }
  }, [getPlaylistAt, currentPlaylistIndex, currentEpisodeIndex, loadEpisode]);

  const nextEpisode = useCallback(() => {
    const playlist = getPlaylistAt(currentPlaylistIndex);
    if (!playlist?.episodes) return;
    if (currentEpisodeIndex > 0) {
      loadEpisode(currentPlaylistIndex, currentEpisodeIndex - 1);
    } else if (currentPlaylistIndex < playlists.length - 1) {
      loadEpisode(currentPlaylistIndex + 1, 0);
    }
  }, [
    getPlaylistAt,
    currentPlaylistIndex,
    currentEpisodeIndex,
    playlists.length,
    loadEpisode,
  ]);

  useEffect(() => {
    nextEpisodeRef.current = nextEpisode;
  }, [nextEpisode]);

  // Register "episode ended" handler
  useEffect(() => {
    setMixcloudOnEnded(() => nextEpisodeRef.current?.());
    return () => setMixcloudOnEnded(null);
  }, [setMixcloudOnEnded]);

  // =======================================================================
  //  trackInfo artwork fallback (unchanged)
  // =======================================================================
  useEffect(() => {
    const incoming = trackInfo?.image;
    if (!incoming) return;
    const normalized = normalizeImage(incoming);
    if (!normalized || normalized === CONFIG.defaultImage) return;
    if (normalized === lastExplicitPlayerImageRef.current) return;
    if (currentEpisodeIndex === -1) {
      preloadAndSetImage(normalized);
    }
  }, [
    trackInfo?.image,
    currentEpisodeIndex,
    preloadAndSetImage,
    normalizeImage,
    CONFIG.defaultImage,
  ]);

  // =======================================================================
  //  RENDER (unchanged from your working version)
  // =======================================================================
  const currentPlaylist = getPlaylistAt(currentPlaylistIndex);
  const currentEpisode = currentPlaylist?.episodes?.[currentEpisodeIndex] || {};
  const episodes = currentPlaylist?.episodes || [];

  return (
    <>
      <div className="pageContainer">
        <section className="news-page-header">
          <h1>Catchup</h1>
          <p>
            Missed your favorite show? Listen to all our previous episodes here!
          </p>
        </section>

        <section className="catchup-player-container">
          <div className="catchup-header">
            <h2>Select Playlist</h2>
          </div>

          <div className="player-layout">
            <div className="player-main">
              <div className="now-playing">
                <img
                  id="current-show-image"
                  key={`${currentPlaylistIndex}-${currentEpisodeIndex}`}
                  className="show-image"
                  src={currentPlaylist.image || CONFIG.defaultImage}
                  alt="Playlist Image"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    if (e.currentTarget.src !== CONFIG.defaultImage) {
                      setPlayerImage(CONFIG.defaultImage);
                      lastExplicitPlayerImageRef.current = CONFIG.defaultImage;
                    }
                    e.currentTarget.src = CONFIG.defaultImage;
                  }}
                />
                <h3 id="current-show-title" className="show-title-catchup">
                  {isLoading ? 'Loading playlists...' : currentPlaylist.title}
                </h3>
                <h4 id="current-episode-title" className="episode-title">
                  {currentEpisodeIndex >= 0
                    ? currentEpisode.title
                    : 'Select an episode to play'}
                </h4>
                <p id="current-show-description" className="show-description">
                  {currentEpisodeIndex >= 0
                    ? currentEpisode.description ||
                      currentPlaylist.description ||
                      'No description available'
                    : 'Select an episode to play'}
                </p>

                {currentEpisodeIndex >= 0 &&
                  currentPlaylist &&
                  currentEpisode && (
                    <button
                      className="share-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        shareEpisode(
                          currentPlaylist.title,
                          currentEpisode.title,
                          currentPlaylist.slug,
                          currentEpisode.slug
                        );
                      }}
                      title="Share this episode"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="18" cy="5" r="3"></circle>
                        <circle cx="6" cy="12" r="3"></circle>
                        <circle cx="18" cy="19" r="3"></circle>
                        <line
                          x1="8.59"
                          y1="13.51"
                          x2="15.42"
                          y2="17.49"
                        ></line>
                        <line
                          x1="15.41"
                          y1="6.51"
                          x2="8.59"
                          y2="10.49"
                        ></line>
                      </svg>
                      Share Episode
                    </button>
                  )}
              </div>
            </div>

            <div className="shows-sidebar">
              <div className="shows-header">
                <span>Playlists</span>
              </div>
              <div className="shows-list" id="shows-list">
                {isLoading ? (
                  <div className="loading">
                    <div className="spinner" />
                    <p>Loading playlists...</p>
                  </div>
                ) : error ? (
                  <div className="no-content">
                    <i className="fas fa-exclamation-triangle" />
                    <p>{error}</p>
                  </div>
                ) : (
                  <>
                    {/* All Episodes — sticky */}
                    <div
                      className={`show-item all-episodes-item ${
                        currentPlaylistIndex === -1 ? 'active' : ''
                      }`}
                      onClick={handleSelectAllEpisodes}
                      style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 3,
                        background:
                          currentPlaylistIndex === -1
                            ? 'rgba(0,0,0,0.55)'
                            : 'rgba(0,0,0,0.4)',
                        borderBottom: '1px solid rgba(255,255,255,0.2)',
                      }}
                    >
                      <img
                        src={allEpisodes[0]?.image || CONFIG.defaultImage}
                        alt="All Episodes"
                        className="show-item-img"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = CONFIG.defaultImage;
                        }}
                      />
                      <div className="show-item-info">
                        <div className="show-item-title">All Episodes</div>
                        <div className="show-item-desc">
                          {allEpisodes.length
                            ? `${allEpisodes.length} episode${
                                allEpisodes.length !== 1 ? 's' : ''
                              }`
                            : 'No episodes yet'}
                        </div>
                      </div>
                    </div>

                    {/* Playlists */}
                    {playlists.length === 0 ? (
                      <div
                        className="no-content"
                        style={{ padding: '20px' }}
                      >
                        <i className="fas fa-headphones-slash" />
                        <p>No playlists configured.</p>
                      </div>
                    ) : (
                      playlists.map((playlist, index) => (
                        <div
                          key={index}
                          className={`show-item ${
                            index === currentPlaylistIndex ? 'active' : ''
                          }`}
                          onClick={() => handleSelectPlaylist(index)}
                        >
                          <img
                            src={playlist.image || CONFIG.defaultImage}
                            alt={playlist.title}
                            className="show-item-img"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = CONFIG.defaultImage;
                            }}
                          />
                          <div className="show-item-info">
                            <div className="show-item-title">
                              {playlist.title}
                            </div>
                            <div className="show-item-desc">
                              {playlist.description || 'No description'}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="episodes-container">
            <div className="episodes-header">
              <span>
                Episodes in{' '}
                <span id="selected-show-name">
                  {currentPlaylist?.title || 'Select a playlist'}
                </span>
              </span>
              <span className="episode-count" id="episode-count">
                {episodes.length
                  ? `${episodes.length} episode${
                      episodes.length !== 1 ? 's' : ''
                    }`
                  : '0 episodes'}
              </span>
            </div>
            <div className="episodes-list" id="episodes-list">
              {episodes.length === 0 ? (
                <div className="no-content">
                  <i className="fas fa-headphones" />
                  <p>No episodes available.</p>
                </div>
              ) : (
                episodes.map((episode, index) => {
                  const isActive = index === currentEpisodeIndex;
                  const isPlayingThis =
                    isActive && globalIsPlaying && isCatchupMode;

                  return (
                    <div
                      key={index}
                      className={`episode-item ${isActive ? 'active' : ''} ${
                        isPlayingThis ? 'playing' : ''
                      }`}
                    >
                      <div
                        className="episode-item-main"
                        onClick={() =>
                          loadEpisode(currentPlaylistIndex, index)
                        }
                      >
                        <div className="episode-item-play">
                          <svg
                            className="control-svg"
                            fill="#BD003A"
                            height="36px"
                            width="36px"
                            version="1.1"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 45.9 45.9"
                          >
                            <g
                              className="PlayMain"
                              transform="translate(0, 0) scale(0.08)"
                              style={{
                                display: isPlayingThis ? 'none' : 'inline',
                              }}
                            >
                              <g className="playGroup">
                                <circle
                                  cx="250"
                                  cy="250"
                                  r="200"
                                  fill="white"
                                />
                                <path
                                  className="cls-play"
                                  d="M229.5,0C102.751,0,0,102.751,0,229.5S102.751,459,229.5,459S459,356.249,459,229.5S356.249,0,229.5,0z M310.292,239.651 l-111.764,76.084c-3.761,2.56-8.63,2.831-12.652,0.704c-4.022-2.128-6.538-6.305-6.538-10.855V153.416 c0-4.55,2.516-8.727,6.538-10.855c4.022-2.127,8.891-1.857,12.652,0.704l111.764,76.084c3.359,2.287,5.37,6.087,5.37,10.151 C315.662,233.564,313.652,237.364,310.292,239.651z"
                                  fill="#BD003A"
                                />
                              </g>
                            </g>
                            <g
                              className="PauseMain"
                              transform="translate(0, 0) scale(0.072)"
                              style={{
                                display: isPlayingThis ? 'inline' : 'none',
                              }}
                            >
                              <g className="pauseGroup">
                                <circle
                                  cx="250"
                                  cy="250"
                                  r="200"
                                  fill="white"
                                />
                                <path
                                  className="cls-pause"
                                  d="M256,0C114.617,0,0,114.617,0,256s114.617,256,256,256s256-114.617,256-256S397.383,0,256,0z M336,320 c0,8.836-7.156,16-16,16H192c-8.844,0-16-7.164-16-16V192c0-8.836,7.156-16,16-16h128c8.844,0,16,7.164,16,16V320z"
                                  fill="#BD003A"
                                />
                              </g>
                            </g>
                          </svg>
                        </div>

                        <div className="episode-item-info">
                          <div className="episode-item-title">
                            {episode.title}
                          </div>
                          <div className="episode-item-desc">
                            {episode.description || 'No description'}
                          </div>
                        </div>
                        <div className="episode-item-duration">
                          {episode.duration || 'N/A'}
                        </div>
                      </div>

                      <button
                        className="episode-share-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          shareEpisode(
                            currentPlaylist.title,
                            episode.title,
                            currentPlaylist.slug,
                            episode.slug
                          );
                        }}
                        title="Share this episode"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="18" cy="5" r="3"></circle>
                          <circle cx="6" cy="12" r="3"></circle>
                          <circle cx="18" cy="19" r="3"></circle>
                          <line
                            x1="8.59"
                            y1="13.51"
                            x2="15.42"
                            y2="17.49"
                          ></line>
                          <line
                            x1="15.41"
                            y1="6.51"
                            x2="8.59"
                            y2="10.49"
                          ></line>
                        </svg>
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};